import {
	autoinject,
	bindable,
	Container,
	customElement,
	PLATFORM,
	useView,
} from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import React from 'react';

import { Card as OUICard } from '~/lib/oui/components/Card/Card';
import { ReactComponent } from '~/components/react-component/react-component';
import utilStyles from '~/styles/util.module.scss';

@autoinject
@customElement('a-card')
@useView(PLATFORM.moduleName('~/components/card/card.html'))
export class Card extends ReactComponent {
	/**
	 * Card title (optional)
	 */
	@bindable public title: string;

	/**
	 * Card subtitle (optional)
	 */
	@bindable public subtitle: string;

	/**
	 * Enable border (defaults to true)
	 */
	@bindable public border: boolean = true;

	/**
	 * Show separator between header and body
	 */
	@bindable public showHeaderBodySeparator: boolean = false;

	/**
	 * Show separator between body and footer
	 */
	@bindable public showBodyFooterSeparator: boolean = false;

	/**
	 * Fluid layout (Defaults to false)
	 * - `true` if card will adjust to contents' width
	 * - `false` if card will expand to parent's width
	 */
	@bindable public fluid: boolean = false;

	/**
	 * Show orb next to card header with value and title (optional)
	 */
	@bindable public headerOrb: HeaderOrb;

	private readonly i18n: I18N = Container.instance.get(I18N);

	constructor(element: Element) {
		super('a-card', element);
	}

	/**
	 * Render the React DOM
	 */
	protected render() {
		this.renderReactDOM(
			<OUICard className="a-card" border={this.border}>
				<OUICard.Header>
					{this.title ? (
						<OUICard.Title>{this.title}</OUICard.Title>
					) : (
						''
					)}
					{this.subtitle ? (
						<OUICard.Subtitle>{this.subtitle}</OUICard.Subtitle>
					) : (
						''
					)}
					{this.headerOrb && this.headerOrb.active && (
						<button
							type="button"
							className={'orb orb-active'}
							title={this.i18n.tr(
								'card.header-orb-count-statement',
								{
									context: this.headerOrb.active.context,
									count: this.headerOrb.active.count,
									prefix: this.headerOrb.active.prefix,
									singularSuffix:
										this.headerOrb.active.singularSuffix,
									pluralSuffix:
										this.headerOrb.active.pluralSuffix,
								},
							)}
						>
							<span aria-hidden="true">
								{this.headerOrb.active.count}
							</span>
							<span className={utilStyles['sr-only']}>
								{this.i18n.tr(
									'card.header-orb-count-statement',
									{
										context: this.headerOrb.active.context,
										count: this.headerOrb.active.count,
										prefix: this.headerOrb.active.prefix,
										singularSuffix:
											this.headerOrb.active
												.singularSuffix,
										pluralSuffix:
											this.headerOrb.active.pluralSuffix,
									},
								)}
							</span>
						</button>
					)}
					{this.headerOrb && this.headerOrb.inactive && (
						<button
							type="button"
							className={'orb orb-inactive'}
							title={this.i18n.tr(
								'card.header-orb-count-statement',
								{
									context: this.headerOrb.inactive.context,
									count: this.headerOrb.inactive.count,
									prefix: this.headerOrb.inactive.prefix,
									singularSuffix:
										this.headerOrb.inactive.singularSuffix,
									pluralSuffix:
										this.headerOrb.inactive.pluralSuffix,
								},
							)}
						>
							<span aria-hidden="true">
								{this.headerOrb.inactive.count}
							</span>
							<span className={utilStyles['sr-only']}>
								{this.i18n.tr(
									'card.header-orb-count-statement',
									{
										context:
											this.headerOrb.inactive.context,
										count: this.headerOrb.inactive.count,
										prefix: this.headerOrb.inactive.prefix,
										singularSuffix:
											this.headerOrb.inactive
												.singularSuffix,
										pluralSuffix:
											this.headerOrb.inactive
												.pluralSuffix,
									},
								)}
							</span>
						</button>
					)}
					<div data-slot="header" />
				</OUICard.Header>
				{this.showHeaderBodySeparator && <OUICard.Separator />}
				<OUICard.Body className="a-card__body">
					<div data-slot="body" />
				</OUICard.Body>
				{this.showHeaderBodySeparator && <OUICard.Separator />}
				<OUICard.Footer className="a-card__footer">
					<div data-slot="footer" />
				</OUICard.Footer>
			</OUICard>,
		);
	}
}

export interface HeaderOrb {
	active: OrbType;
	inactive: OrbType;
}

interface OrbType {
	context: string;
	count: number;
	prefix: string;
	singularSuffix: string;
	pluralSuffix: string;
}
